<script>
  import Page from '@components/page.svelte'
  import Table from '@components/table'
  import FilterBar from '@components/table/filter-bar.svelte'
  import Image from '@components/general/image.svelte'
  import Button from '@components/buttons/button.svelte'
  import Dropdown from '@components/general/dropdown.svelte'
  import Form from '@components/contributors/form.svelte'
  import { usePageParam } from '@hooks/use-page-param.svelte'
  import { useSelection } from '@hooks/use-selection.svelte'
  import { router } from '@inertiajs/svelte'
  import { CirclePlus, EllipsisVertical, Pencil, Trash2 } from 'lucide-svelte'
  import { sortItems } from '@lib/utils'

  let { contributors = [], pagination, ids = [] } = $props()

  let selection = $derived(useSelection({ initialItems: contributors, allIds: ids }))
  let sort = usePageParam({ key: "sort", defaultValue: "newest", items: sortItems })

  let formOpen = $state(false)
  let selectedContributor = $state(null)

  let pageProps = {
    title: "Contributors",
    subtitle: "Manage your contributors",
    links: [{ text: "Admin", href: "/admin" }]
  }

  function selectContributor(contributor) {
    selectedContributor = contributor
    formOpen = true
  }

  function deleteContributor(contributor) {
    if (!window.confirm(`Are you sure you want to delete ${contributor.name}?`)) return
    router.delete(`/admin/contributors/${contributor.id}`)
  }

  function clearContributor() {
    selectedContributor = null
  }

</script>

<Page {...pageProps}>

  {#snippet buttons()}
    <Button text="New contributor" onclick={() => formOpen = true} Icon={CirclePlus} />
  {/snippet}

  <FilterBar {pagination} paginationProps={{ summary: true, terms: ["contributor", "contributors"] }}>
    {#snippet filters()}

      <!-- Sort -->
      <Dropdown buttonProps={{ text: sort.label || "Sort", size: "md", Icon: sort.icon }} width="16rem" align="start" itemSize="sm">
        {#snippet contents(item)}
          {#each sort.items as sortItem (sortItem.value)}
            {@render item({ text: sortItem.label, icon: sortItem.icon, onSelect: () => sort.setValue(sortItem.value) })}
          {/each}
        {/snippet}
      </Dropdown>

    {/snippet}
  </FilterBar>

  <Table.Root empty={contributors.length === 0} emptyMessage="No contributors found">
    <Table.Head>
      <Table.SelectionDropdownCell {selection} />
      <Table.Header width="5%"></Table.Header>
      <Table.Header width="22%">Name</Table.Header>
      <Table.Header width="18%">Role</Table.Header>
      <Table.Header width="35%">Bio</Table.Header>
      <Table.Header width="10%" align="center">Actions</Table.Header>
    </Table.Head>

    <Table.Body>
      {#each contributors as contributor (contributor.id)}
        <Table.Row>
          <Table.Cell width="3rem" align="center">
            <input type="checkbox" checked={selection.has(contributor.id)} onclick={e => selection.toggle(contributor.id, e)} />
          </Table.Cell>
          <Table.Cell width="5%">
            <Image src={contributor.image} alt={contributor.name} 
              loading="lazy" text={contributor.name.charAt(0)}
              class="size-8 border border-gray-200/70 bg-gray-100 rounded-full" />
          </Table.Cell>
          <Table.Cell width="22%">
            <Button variant="link" text={contributor.name} onclick={() => selectContributor(contributor)} />
          </Table.Cell>
          <Table.Cell width="18%">{contributor.role}</Table.Cell>
          <Table.Cell width="35%">
            <div class="max-w-80 truncate">{contributor.bio?.text}</div>
          </Table.Cell>
          <Table.Cell width="10%" align="center">
            <Dropdown buttonProps={{ variant: "naked", Icon: EllipsisVertical }} showTriggerButton={false} width="16rem">
              {#snippet contents(item)}
                {@render item({ text: "Edit", icon: Pencil, onSelect: () => selectContributor(contributor) })}
                {@render item({ text: "Delete", icon: Trash2, onSelect: () => deleteContributor(contributor), disabled: contributor.id === 1 })}
              {/snippet}
            </Dropdown>
          </Table.Cell>
        </Table.Row>
      {/each}
    </Table.Body>
  </Table.Root>

  <Table.Pagination {pagination} paginationProps={{ summary: true, terms: ["contributor", "contributors"] }} />

</Page>

<Form bind:open={formOpen} contributor={selectedContributor} onClose={() => selectedContributor = null} />